import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Truck, Map, DollarSign, Star, Bell, Check, X, Navigation, ArrowRight, Repeat, Clock, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { SupplierLayout } from '../../components/SupplierLayout';
import { useSupplierAuth } from '../../lib/supplierAuthStore';
import { useSupplierStore, MOCK_TRUCKS } from '../../lib/supplierStore';
export function SupplierDashboard() {
  const {
    user
  } = useSupplierAuth();
  const navigate = useNavigate();
  const {
    requests,
    acceptRequest,
    declineRequest
  } = useSupplierStore();
  const pendingRequests = requests.filter(r => r.status === 'pending').slice(0, 4);
  const handleAccept = (id: string) => {
    const newJobId = acceptRequest(id);
    if (newJobId) {
      toast.success('Request accepted! Job created.');
      navigate(`/supplier/dispatcher?jobId=${newJobId}`);
    }
  };
  const handleDecline = (id: string) => {
    declineRequest(id);
    toast.success('Request declined.');
  };
  const activeTrucks = MOCK_TRUCKS.filter(t => t.status === 'Active' || t.status === 'In service').length;
  const utilization = Math.round(activeTrucks / MOCK_TRUCKS.length * 100);
  return <SupplierLayout data-id="element-2941">
      <div className="max-w-6xl mx-auto space-y-8" data-id="element-2942">
        {/* KPI Strip */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-2943">
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2944">
            <div className="flex items-center gap-3 mb-2" data-id="element-2945">
              <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center" data-id="element-2946">
                <Truck className="w-4 h-4" data-id="element-2947" />
              </div>
              <p className="text-sm font-bold text-gray-500" data-id="element-2948">Active Jobs</p>
            </div>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-2949">12</p>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2950">
            <div className="flex items-center gap-3 mb-2" data-id="element-2951">
              <div className="w-8 h-8 rounded-lg bg-green-50 text-green-600 flex items-center justify-center" data-id="element-2952">
                <DollarSign className="w-4 h-4" data-id="element-2953" />
              </div>
              <p className="text-sm font-bold text-gray-500" data-id="element-2954">Revenue (Week)</p>
            </div>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-2955">
              AED 14.2K
            </p>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2956">
            <div className="flex items-center gap-3 mb-2" data-id="element-2957">
              <div className="w-8 h-8 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center" data-id="element-2958">
                <Bell className="w-4 h-4" data-id="element-2959" />
              </div>
              <p className="text-sm font-bold text-gray-500" data-id="element-2960">
                Pending Requests
              </p>
            </div>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-2961">3</p>
          </div>
          <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm" data-id="element-2962">
            <div className="flex items-center gap-3 mb-2" data-id="element-2963">
              <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center" data-id="element-2964">
                <Map className="w-4 h-4" data-id="element-2965" />
              </div>
              <p className="text-sm font-bold text-gray-500" data-id="element-2966">
                Fleet Utilization
              </p>
            </div>
            <p className="text-3xl font-display font-bold text-gray-900" data-id="element-2967">
              {utilization}%
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-2968">
          {/* Left Col - Incoming Jobs */}
          <div className="lg:col-span-2 space-y-4" data-id="element-2969">
            <div className="flex items-center justify-between" data-id="element-2970">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-2971">Live Requests</h2>
              <Link to="/supplier/requests" className="text-sm font-bold text-brand-600 hover:text-brand-700" data-id="element-2972">
                View all →
              </Link>
            </div>

            {pendingRequests.length === 0 ? <div className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm text-center" data-id="element-2973">
                <Bell className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-2974" />
                <p className="text-sm font-medium text-gray-900" data-id="element-2975">
                  No pending requests
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-2976">
                  New customer requests will appear here.
                </p>
              </div> : pendingRequests.map((req, i) => {
            const isCounter = req.subtype === 'counter';
            const hasOffer = req.customerOffer !== undefined && req.customerOffer !== req.originalPrice;
            const delta = hasOffer ? req.customerOffer! - req.originalPrice : 0;
            return <motion.div key={req.id} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: i * 0.08
            }} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm flex flex-col md:flex-row gap-4" data-id="element-2977">
                    <div className="flex-1 min-w-0" data-id="element-2978">
                      {/* Badges row */}
                      <div className="flex items-center gap-2 mb-2 flex-wrap" data-id="element-2979">
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${req.type === 'bidding' ? 'bg-purple-50 text-purple-700' : 'bg-brand-50 text-brand-700'}`} data-id="element-2980">
                          {req.type === 'bidding' ? 'Bidding' : 'Direct'}
                        </span>
                        {isCounter && <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider" data-id="element-2981">
                            <Repeat className="w-3 h-3" data-id="element-2982" />
                            Counter-offer
                          </span>}
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500" data-id="element-2983">
                          <Clock className="w-3 h-3" data-id="element-2984" />
                          {req.requestedTime}
                        </span>
                      </div>

                      <h3 className="font-bold text-gray-900 text-base mb-0.5 truncate" data-id="element-2985">
                        {req.serviceType}
                      </h3>
                      <p className="text-sm text-gray-600 mb-3 truncate" data-id="element-2986">
                        {req.customerName}
                      </p>

                      {/* Route */}
                      <div className="flex items-center gap-2 text-xs font-medium text-gray-700 bg-gray-50 px-3 py-2 rounded-xl" data-id="element-2987">
                        <Navigation className="w-3 h-3 text-brand-500 shrink-0" data-id="element-2988" />
                        <span className="truncate" data-id="element-2989">{req.pickup}</span>
                        <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" data-id="element-2990" />
                        <span className="truncate" data-id="element-2991">{req.dropoff}</span>
                      </div>
                    </div>

                    {/* Price + actions panel */}
                    <div className="md:w-44 shrink-0 flex flex-col justify-center border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0 md:pl-4" data-id="element-2992">
                      {hasOffer ? <div className="mb-3" data-id="element-2993">
                          <div className="flex items-baseline gap-2" data-id="element-2994">
                            <p className="text-xl font-display font-bold text-gray-900" data-id="element-2995">
                              AED {req.customerOffer!.toLocaleString()}
                            </p>
                            {delta < 0 && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-red-600" data-id="element-2996">
                                <TrendingDown className="w-3 h-3" data-id="element-2997" />
                                {Math.abs(delta).toLocaleString()}
                              </span>}
                          </div>
                          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 line-through" data-id="element-2998">
                            AED {req.originalPrice.toLocaleString()}
                          </p>
                        </div> : <div className="mb-3" data-id="element-2999">
                          <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-0.5" data-id="element-3000">
                            Suggested
                          </p>
                          <p className="text-xl font-display font-bold text-gray-900" data-id="element-3001">
                            AED {req.originalPrice.toLocaleString()}
                          </p>
                        </div>}
                      <div className="flex gap-2" data-id="element-3002">
                        <button onClick={() => handleDecline(req.id)} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Decline request" data-id="element-3003">
                          <X className="w-3.5 h-3.5" data-id="element-3004" /> Decline
                        </button>
                        <button onClick={() => handleAccept(req.id)} className="flex-1 py-2 bg-gray-900 hover:bg-black text-white rounded-lg font-bold transition-colors flex items-center justify-center gap-1 text-xs" aria-label="Accept request" data-id="element-3005">
                          <Check className="w-3.5 h-3.5" data-id="element-3006" /> Accept
                        </button>
                      </div>
                    </div>
                  </motion.div>;
          })}
          </div>

          {/* Right Col - Quick Stats & Map */}
          <div className="space-y-6" data-id="element-3007">
            <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-id="element-3008">
              <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500 mb-4" data-id="element-3009">
                Quick Stats
              </h2>
              <div className="space-y-4" data-id="element-3010">
                <div className="flex items-center justify-between" data-id="element-3011">
                  <span className="text-sm text-gray-600" data-id="element-3012">Avg Rating</span>
                  <div className="flex items-center gap-1 font-bold text-gray-900" data-id="element-3013">
                    <Star className="w-4 h-4 text-amber-400 fill-current" data-id="element-3014" /> 4.8
                  </div>
                </div>
                <div className="flex items-center justify-between" data-id="element-3015">
                  <span className="text-sm text-gray-600" data-id="element-3016">Completion Rate</span>
                  <span className="font-bold text-gray-900" data-id="element-3017">98.5%</span>
                </div>
                <div className="flex items-center justify-between" data-id="element-3018">
                  <span className="text-sm text-gray-600" data-id="element-3019">Top Route</span>
                  <span className="font-bold text-gray-900 text-sm" data-id="element-3020">
                    DXB → AUH
                  </span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm flex flex-col" data-id="element-3021">
              <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-3022">
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-500" data-id="element-3023">
                  Live Fleet
                </h2>
                <Link to="/supplier/tracking" className="text-xs font-bold text-brand-600" data-id="element-3024">
                  View map
                </Link>
              </div>
              <div className="h-48 relative bg-gray-200" data-id="element-3025">
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=800')] bg-cover bg-center opacity-60 grayscale" data-id="element-3026" />
                <div className="absolute inset-0 bg-brand-900/10 mix-blend-multiply" data-id="element-3027" />

                {/* Fake pins */}
                <div className="absolute top-1/4 left-1/3 w-3 h-3 bg-brand-500 rounded-full border-2 border-white shadow-md" data-id="element-3028"></div>
                <div className="absolute top-1/2 left-1/2 w-3 h-3 bg-brand-500 rounded-full border-2 border-white shadow-md" data-id="element-3029"></div>
                <div className="absolute bottom-1/3 right-1/4 w-3 h-3 bg-brand-500 rounded-full border-2 border-white shadow-md" data-id="element-3030"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SupplierLayout>;
}